/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-empty-object-type */
"use client";

import * as X from "@/ui";
import type { Article } from "@/providers/strapi";
import { ArticleCard } from "./ArticleCard";
import { MaxArticles } from "./config";
import ReactMarkdown from "react-markdown";


export function ArticleList(props: X.PageProps<{
  articles: Article[];
  title?: string;
  moreArticleCta: string;
  moreArticleCtaLoadingState: string;
  button?: (
    | { type: "link"; label: string; href: X.PageHref }
    | { type: "more"; categorySlug: null | string }
  );
}>) {
  // const tr = X.useTranslator();
  // const allArticles = useBlogTagPage('allArticles');
  // const moreArticleCta = useBlogTagPage('moreArticleCta')
  // const moreArticleCtaLoadingState = useBlogTagPage('moreArticleCtaLoadingState')
  const articlesQuery = useArticlesQuery(
    props.articles,
    props.button?.type === "more",
    {
      categorySlug: props.button?.type === "more" ? props.button.categorySlug : null,
      locale: props.locale,
    }
  );

  return (
    <section className="w-full py-8 pb-8">
      <div className="mx-mobile-margin xl:max-w-content xl:mx-auto">
        {props.title &&
          // <h2 className="text-[20px] font-semibold mb-4 xl:mb-6 xl:mt-2">{props.title}</h2>
          <ReactMarkdown components={{
            p: ({ ...props }) => <h2 {...props} className="text-[20px] font-semibold mb-4 xl:mb-6 xl:mt-2" />,
            h1: ({ ...props }) => <h2 {...props} className="text-[20px] font-semibold mb-4 xl:mb-6 xl:mt-2" />,
            h2: ({ ...props }) => <h2 {...props} className="text-[20px] font-semibold mb-4 xl:mb-6 xl:mt-2" />,
            h3: ({ ...props }) => <h2 {...props} className="text-[20px] font-semibold mb-4 xl:mb-6 xl:mt-2" />,
            h4: ({ ...props }) => <h2 {...props} className="text-[20px] font-semibold mb-4 xl:mb-6 xl:mt-2" />,
          }}>
            {props.title}
          </ReactMarkdown>
        }
        <div className="grid xl:grid-cols-4 gap-6 gap-y-10">
          {articlesQuery.articles.map((article, index) => (
            <ArticleCard key={index} article={article} locale={props.locale} />
          ))}
        </div>
        {(props.button && (props.button.type !== "more" || articlesQuery.canLoadMore)) && (
          <div className="flex xl:justify-center mt-10 mb-4 xl:mb-0">
            {props.button.type === "more" ? (
              <X.Button
                secondary lg desktopMd
                disabled={articlesQuery.isLoading}
                onClick={articlesQuery.loadMore}
                label={articlesQuery.isLoading
                  ? props.moreArticleCtaLoadingState //tr({ en: "Loading...", fr: "Chargement..." })
                  : props.moreArticleCta//tr({ en: "Show more", fr: "Charger plus d'articles" })
                }
              />

            ) : (
              <X.Button
                secondary lg desktopMd
                label={props.button.label}
                href={props.button.href}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
}


type FetchArticlesParam = {
  categorySlug: string | null;
  locale: X.Locale;
  start: number;
  limit: number;
};


export function useArticlesQuery(
  initialArticles: Article[],
  initialCanLoadMore: boolean,
  config: Pick<FetchArticlesParam, "locale" | "categorySlug">
) {
  const [param, setParam] = X.useState<FetchArticlesParam>({
    ...config,
    start: initialArticles.length,
    limit: MaxArticles,
  });

  const [canLoadMore, setCanLoadMore] = X.useState(initialCanLoadMore);
  const [articles, setArticles] = X.useState<Article[]>(initialArticles);
  const [isLoading, setLoading] = X.useState(false);
  const [error, setError] = X.useState(false);
  const ref = X.useRef<{} | null>(null);

  const loadMore = async () => {
    if (ref.current || !canLoadMore) {
      return;
    }

    const id = {};
    ref.current = id;

    try {

      setError(false);
      setLoading(true);

      const searchParams = new URLSearchParams();

      searchParams.set("locale", param.locale);
      searchParams.set("start", param.start.toString());
      searchParams.set("limit", (param.limit + 1).toString());

      if (param.categorySlug) {
        searchParams.set("categorySlug", param.categorySlug ?? undefined);
      }

      const response = await fetch(`/api/blog/articles?${searchParams.toString()}`);

      if (!response.ok) {
        setError(true);
        return;
      }

      type Result = { ok: false } | { ok: true; articles: Article[] };
      const result: Result = await response.json();

      if (result.ok && ref.current === id) {
        setCanLoadMore(result.articles.length > param.limit);

        setParam(param => ({
          ...param,
          start: param.start + param.limit,
        }));

        setArticles(articles => [
          ...articles,
          ...result.articles.slice(0, param.limit),
        ]);
        return;
      }

    } catch (err) {
      setError(true);

    } finally {
      setLoading(false);
      if (ref.current === id) {
        ref.current = null;
      }
    }
  };

  return {
    error,
    articles,
    isLoading,
    canLoadMore,
    loadMore,
  };
}
